import { IconProps } from '@tamagui/helpers-icon';
import { LineChart, Percent } from '@tamagui/lucide-icons';
import { Form } from '@unistocks/components/form/form';
import { oneOf, requiredSchema } from '@unistocks/components/form/validators';
import { Card } from '@unistocks/components/layout/card';
import { Column } from '@unistocks/components/layout/flex';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { ProgressBar } from '@unistocks/components/progress-bar/progress-bar';
import { H4, Paragraph } from '@unistocks/components/text/text';
import {
  SavingsGoalLayout,
  savingsGoalLayoutOptions
} from '@unistocks/features/dashboard/components/dashboard-data/savings-goal/layout';
import { t } from '@unistocks/localization';
import { DashboardItemSettingsDto } from '@unistocks/proxy/axios-client';
import { useSavingsGoalsGetListQuery } from '@unistocks/proxy/axios-client/SavingsGoalsQuery';
import { translateEnumOptions } from '@unistocks/shared/utils/enum-utils';
import { extractOptionValue } from '@unistocks/shared/utils/list-utils';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';

export const savingsGoalSchemaFactory = () =>
  z.object({
    title: z.string().nullish(),
    savingsGoal: requiredSchema(z.string(), t('::SavingsGoal')),
    layout: oneOf(
      savingsGoalLayoutOptions.map(({ value }) => value),
      t('::ValidationError:Required:Selection', { 0: t('::Layout') })
    )
  });

export const SavingsGoalSettingsContentStep: React.FC = () => {
  const { data: savingsGoals } = useSavingsGoalsGetListQuery({
    select: ({ items }) => items.map(({ id, displayName }) => ({ key: displayName, value: id }))
  });
  return (
    <>
      <Form.Input
        name={'title'}
        label={t('::DisplayName')}
        helpText={t('::Dashboard:Settings:Title:HelpText')}
      />
      <Form.Select
        name={'savingsGoal'}
        label={t('::SavingsGoal')}
        placeholder={t('::SelectWhat', {
          0: t('::SavingsGoal').toLocaleLowerCase()
        })}
        options={savingsGoals ?? []}
        isRequired
        keyExtractor={extractOptionValue}
      />
    </>
  );
};

export const SavingsGoalSettingsLayoutStep: React.FC = () => {
  const { setValue } = useFormContext<DashboardItemSettingsDto>();
  const layout = useWatch<DashboardItemSettingsDto, 'layout'>({ name: 'layout' });

  return (
    <Column gap={'$3'} mb={'$3'}>
      {translateEnumOptions(savingsGoalLayoutOptions).map(({ key, value }) => {
        const isSelected = layout === value;
        let description: string, Icon: React.ComponentType<IconProps>;

        switch (value) {
          case SavingsGoalLayout.Chart:
            description = t('::Dashboard:Settings:SavingsGoal:Layout:Chart:Description');
            Icon = LineChart;
            break;
          case SavingsGoalLayout.ProgressBar:
            description = t('::Dashboard:Settings:SavingsGoal:Layout:ProgressBar:Description');
            Icon = Percent;
            break;
        }

        return (
          <Card
            key={value}
            pressable
            hoverable
            bordered
            group
            bg={isSelected ? '$color4' : 'transparent'}
            onPress={() => setValue('layout', value)}
          >
            <Card.Header>
              <H4>{key}</H4>
              <HeaderIcon Icon={Icon} color={'$headingFont'} />
            </Card.Header>
            <Card.Body>
              {value === SavingsGoalLayout.ProgressBar ? (
                <ProgressBar
                  value={0.55}
                  $group-hover={{
                    bg: isSelected ? '$color4' : '$color5'
                  }}
                />
              ) : null}
              <Paragraph>{description}</Paragraph>
            </Card.Body>
          </Card>
        );
      })}
    </Column>
  );
};

SavingsGoalSettingsContentStep.displayName = 'SavingsGoalSettingsContentStep';
SavingsGoalSettingsLayoutStep.displayName = 'SavingsGoalSettingsLayoutStep';
